import * as React from 'react';

import classNames from 'classnames';

import type {LeadStatus} from 'nutshell-graphql-types';

import {colors} from '../../colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from '../../icon/svg/loading-icon';
import {Body} from '../../typography/body';
import {useRefObserver} from '../../utils/hooks/use-ref-observer';

import {useDelayedHoverVisibility} from './hooks/use-delayed-hover-visibility';

import styles from './stage.module.css';

type Props = {
    stageName: string;
    // Whether the stage should be visually active. Past stages should be shown
    // as active, even if they are not the current stage.
    isActive?: boolean;
    // Whether the lead is currently in this stage.
    isCurrentStage?: boolean;
    // Whether the stage in the process of being advanced, purely visual.
    isSelected?: boolean;
    onClick?: () => void;
    isStart?: boolean;
    isEnd?: boolean;
    isHot?: boolean;
    isLoading?: boolean;
    status?: LeadStatus;
    isSmall?: boolean;
    // If any other stages are currently hovered, show the hover display
    // instantly when hovering this one
    isHoverInstant?: boolean;
    // We need to let the carousel know when we're hovering so subsequent
    // stage hovers are instant / delayed accordingly
    onHoverDisplayChange?: (arg1: boolean) => void;
    // Callback to notify parent when popover should be shown/hidden
    onPopoverVisibilityChange?: (
        shouldShow: boolean,
        ref?: HTMLButtonElement | null | undefined
    ) => void;
    isDisplayOnly?: boolean;
};

export function Stage(props: Props) {
    const {isStart = false, isEnd = false, status = 'OPEN', isSmall = false} = props;
    const {isHoverInstant, onHoverDisplayChange, onPopoverVisibilityChange} = props;

    const stageRef = React.useRef<HTMLButtonElement | null | undefined>(null);

    const {isHovered} = useRefObserver(stageRef, ['isHovered']);

    const shouldShowPopover = useDelayedHoverVisibility({
        isHovered: Boolean(isHovered),
        isInstant: Boolean(isHoverInstant),
        delayMs: 200,
    });

    // Notify parent when our hover display visibility changes
    React.useEffect(() => {
        if (typeof onHoverDisplayChange === 'function') {
            onHoverDisplayChange(shouldShowPopover);
        }

        return () => {
            // On unmount, if we were shown, inform parent that we no longer are
            if (typeof onHoverDisplayChange === 'function' && shouldShowPopover) {
                onHoverDisplayChange(false);
            }
        };
    }, [shouldShowPopover, onHoverDisplayChange]);

    // Notify parent when popover visibility changes
    React.useEffect(() => {
        if (onPopoverVisibilityChange) {
            onPopoverVisibilityChange(shouldShowPopover, stageRef.current);
        }
    }, [shouldShowPopover, onPopoverVisibilityChange]);

    const isInactive = status === 'LOST' || status === 'PENDING' || status === 'CANCELLED';
    const shouldUnderline = !props.isCurrentStage && (isHovered || false) && !props.isDisplayOnly;

    let bodyColor = 'dark';
    if (!isInactive && (props.isActive || props.isCurrentStage)) {
        bodyColor = 'white';
    }

    return (
        <button
            onClick={props.onClick}
            // @ts-expect-error
            ref={stageRef}
            disabled={props.isCurrentStage}
            className={classNames(styles['stage-container'], {
                [styles['stage-container--active']]: props.isActive,
                [styles['stage-container--selected']]: props.isSelected,
                [styles['stage-container--hot']]: props.isHot,
                [styles['stage-container--current']]: props.isCurrentStage,
                [styles['stage-container--won']]: status === 'WON',
                [styles['stage-container--inactive']]: isInactive,
                [styles['stage-container--small']]: isSmall,
                [styles['stage-container--display-only']]: props.isDisplayOnly,
            })}
        >
            {!isStart && <div className={styles['stage-left']} />}
            <div
                className={classNames(styles['stage-center'], {
                    [styles['stage-center--start']]: isStart,
                    [styles['stage-center--end']]: isEnd,
                    [styles['stage-center--small']]: isSmall,
                })}
            >
                {props.isLoading ? (
                    <LoadingIcon size={24} fill={colors.grey} />
                ) : (
                    <Body
                        truncate={{type: 'inline'}}
                        size='small'
                        // @ts-expect-error
                        color={bodyColor}
                        underline={shouldUnderline}
                    >
                        {props.stageName}
                    </Body>
                )}
            </div>
            {!isEnd && <div className={styles['stage-right']} />}
        </button>
    );
}
